import {
  createTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      paper: '#121212',
      default: '#0d0d0d',
    },
  },
});

export default function ConsolidationItemModal(props) {
  const {
    open,
    onClose,
    item,
    labelImage,
    isLoadingLabelImage,
    allPredictions,
    predictionTypes,
  } = props;

  const { t } = useTranslation();

  const [predictions, setPredictions] = useState([]);

  useEffect(() => {
    if (allPredictions && item) {
      const myPredictions = allPredictions.filter(
        prediction => prediction.detection_id === item.id,
      );
      setPredictions(myPredictions);
    }
  }, [allPredictions, item]);

  useEffect(() => {
    if (labelImage) {
      const canvas = document.getElementById('labelCanvas');
      if (canvas) {
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.src = labelImage;
        img.onload = function () {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);

          if (item?.type === 'label' || item?.type === 'promotion') {
            predictions.forEach(prediction => {
              ctx.strokeStyle = 'red';
              if (prediction.coords && prediction.coords.length > 0) {
                const path = new Path2D();
                const [first, ...rest] = prediction.coords;
                path.moveTo(...first);
                rest.forEach(point => path.lineTo(...point));
                path.closePath();
                ctx.stroke(path);
              }
            });
          }
        }
      };
    }
  }, [labelImage]);

  if (!item) {
    return null;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#121212', // fondo oscuro
            color: '#fff', // texto claro
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant="h6" component="div">
            {t('hip_app.general.item_info')}
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <IconComponent iconName={'close-outline'} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div" gutterBottom>
            <b>{t('hip_app.general.detection_id')}:</b> {item.id}
          </Typography>
          <Typography variant="body1" component="div">
            <b>{t('hip_app.general.item_type')}:</b> {item.type},{' '}
            {item.stageName}
          </Typography>
          {isLoadingLabelImage ? (
            <LinearProgress />
          ) : (
            <div>
              {labelImage?.includes('https') ? (
                <canvas id="labelCanvas" style={{ width: '80%', height: 'auto' }}/>
              ) : (
                <Typography>
                  {t('hip_app.general.no_image_found', 'No image found')}
                </Typography>
              )}
            </div>
          )}
          <Typography variant="body1" component="div">
            <b>{t('hip_app.general.predictions', 'Predictions')}:</b>
          </Typography>
          <Table sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {t('hip_app.general.type', 'Type')}
                </TableCell>
                <TableCell align="center">
                  {t('hip_app.general.value', 'Value')}
                </TableCell>
                <TableCell align="center">
                  {t('hip_app.general.confidence', 'Confidence')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {predictions.map(prediction => (
                <TableRow key={prediction.prediction_type}>
                  <TableCell align="center">
                    {predictionTypes[prediction.prediction_type]} (
                    {prediction.prediction_type})
                  </TableCell>
                  <TableCell align="center">{prediction.values}</TableCell>
                  {prediction.confidences.length === 1 ? (
                    <TableCell align="right">
                      {prediction.confidences[0].toFixed(3)}
                    </TableCell>
                  ) : (
                    <TableCell align="right">
                      {prediction.confidences}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body1" component="div">
            <b>{t('hip_app.general.item_metadata', 'Item metadata')}:</b>
          </Typography>
          <pre>
            <code>{JSON.stringify(item, null, 2)}</code>
          </pre>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
